// ============================================
// pages/transportation/TransportBillForm.tsx
// Create/edit a transport bill (header). The line items come from
// transport-trips that are aggregated via the "Aggregate" action on the list.
// ============================================
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, XCircle, Receipt, Layers, Truck, FileUp, FileText, Trash2 } from 'lucide-react';
import apiClient from '../../api/apiClient';
import {
  seasonsAPI, districtsAPI, commoditiesAPI, htContractorsAPI, transportBillsAPI,
  transportBillPaymentsAPI, transportTripsAPI,
} from '../../api/services';
import { ServicePaymentsSection } from '../../components/ServicePaymentsSection';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole, formatAmount, num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';

const empty = {
  season_id: 0,
  commodity_id: 0,
  district_id: 0,
  contractor_id: 0,
  bill_no: '',
  bill_date: '',
  billing_period_start: '',
  billing_period_end: '',
  total_trips: 0,
  total_bags: 0,
  total_weight_qtl: 0,
  total_amount_claimed: 0,
  total_amount_verified: 0,
  payment_released: 0,
  tds_rate: 2,          // default TDS % per Section 194C for transport contractors
  tds_amount: 0,
  balance_to_release: 0,
  remarks: '',
};

export const TransportBillForm: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isCreate = !idParam || idParam === 'new';
  const recordId = isCreate ? null : parseInt(idParam);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [contractors, setContractors] = useState<any[]>([]);
  const [form, setForm] = useState<any>(empty);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [linkableTrips, setLinkableTrips] = useState<any[]>([]);
  const [selectedTripIds, setSelectedTripIds] = useState<Set<number>>(new Set());
  const [tripsLoading, setTripsLoading] = useState(false);
  const [linkSaving, setLinkSaving] = useState(false);

  const [invoiceUploading, setInvoiceUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const isLocked = ['submitted', 'dm_verified', 'manager_approved'].includes(status);
  const isDM = user?.role === UserRole.DM;

  // Live preview totals computed from the currently ticked trips.
  // Backend remains the source of truth on Save/Link.
  const selectedTotals = React.useMemo(() => {
    const ticked = linkableTrips.filter((t: any) => selectedTripIds.has(t.id));
    return {
      total_trips: ticked.length,
      total_bags: ticked.reduce((s, t: any) => s + num(t.bags_sent), 0),
      total_weight_qtl: +ticked.reduce((s, t: any) => s + num(t.weight_qtl), 0).toFixed(3),
      total_amount_claimed: +ticked.reduce((s, t: any) => s + num(t.amount_claimed), 0).toFixed(2),
      total_amount_verified: +ticked.reduce((s, t: any) => s + num(t.amount_verified), 0).toFixed(2),
    };
  }, [linkableTrips, selectedTripIds]);

  // Show the preview whenever the user is actively picking
  // (linkableTrips loaded AND at least some trips visible). Otherwise, use saved totals from form.
  const displayTotals = linkableTrips.length > 0 ? selectedTotals : {
    total_trips: num(form.total_trips),
    total_bags: num(form.total_bags),
    total_weight_qtl: num(form.total_weight_qtl),
    total_amount_claimed: num(form.total_amount_claimed),
    total_amount_verified: num(form.total_amount_verified),
  };

  useEffect(() => {
    (async () => {
      try {
        const [s, d, c, ht] = await Promise.all([
          seasonsAPI.list(), districtsAPI.list(), commoditiesAPI.list(), htContractorsAPI.list(),
        ]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        setDistricts(d.data);
        setCommodities(c.data);
        setContractors(ht.data);
        const active = list.find((x) => x.is_active) || list[0];
        if (isCreate) {
          setForm((f: any) => ({
            ...f,
            season_id: active?.id || 0,
            district_id: isDM ? user?.district_id || 0 : 0,
          }));
        } else if (recordId && active) {
          try {
            const res = await transportBillsAPI.get(active.id, recordId);
            const raw = res.data as any;
            setForm({ ...empty, ...raw });
            setStatus(raw.status || 'draft');
          } catch (err: any) {
            setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load bill' });
          }
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load data' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch trips available for linking. Works in both create and edit modes —
  // create mode omits bill_id so only unlinked trips are returned.
  const loadLinkableTrips = async () => {
    if (!form.season_id || !form.commodity_id || !form.district_id || !form.contractor_id) {
      setLinkableTrips([]);
      return;
    }
    setTripsLoading(true);
    try {
      const params: any = {
        commodity_id: form.commodity_id,
        district_id: form.district_id,
        contractor_id: form.contractor_id,
      };
      if (recordId) params.bill_id = recordId;
      if (form.billing_period_start) params.period_start = form.billing_period_start;
      if (form.billing_period_end) params.period_end = form.billing_period_end;
      const res = await transportTripsAPI.listLinkable(form.season_id, params);
      const list = Array.isArray(res.data) ? res.data : [];
      setLinkableTrips(list);
      if (recordId) {
        setSelectedTripIds(new Set(list.filter((t: any) => t.transport_bill_id === recordId).map((t: any) => t.id)));
      }
    } catch {
      setLinkableTrips([]);
    } finally {
      setTripsLoading(false);
    }
  };

  useEffect(() => {
    loadLinkableTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, form.season_id, form.commodity_id, form.district_id, form.contractor_id, form.billing_period_start, form.billing_period_end]);

  const toggleTrip = (id: number) => {
    setSelectedTripIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleInvoiceSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || isCreate || !recordId) return;
    e.target.value = '';
    setInvoiceUploading(true);
    setMessage(null);
    try {
      const res = await transportBillsAPI.uploadInvoice(form.season_id, recordId, file);
      setForm((f: any) => ({ ...f, ...(res.data as any) }));
      setMessage({ type: 'success', text: 'Invoice uploaded' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to upload invoice' });
    } finally {
      setInvoiceUploading(false);
    }
  };

  const handleInvoiceView = async () => {
    if (isCreate || !recordId) return;
    try {
      const res = await apiClient.get(transportBillsAPI.invoiceUrl(form.season_id, recordId), {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: (res.headers as any)['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to open invoice' });
    }
  };

  const handleInvoiceDelete = async () => {
    if (isCreate || !recordId) return;
    if (!window.confirm('Remove the uploaded invoice?')) return;
    try {
      const res = await transportBillsAPI.deleteInvoice(form.season_id, recordId);
      setForm((f: any) => ({ ...f, ...(res.data as any), invoice_file_path: null, invoice_file_name: null, invoice_uploaded_at: null }));
      setMessage({ type: 'success', text: 'Invoice removed' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to remove invoice' });
    }
  };

  const handleLinkAndAggregate = async () => {
    if (isCreate || !recordId) return;
    setLinkSaving(true);
    setMessage(null);
    try {
      const res = await transportBillsAPI.linkTrips(form.season_id, recordId, Array.from(selectedTripIds));
      const updated = res.data as any;
      setForm((f: any) => ({ ...f, ...updated }));
      setMessage({ type: 'success', text: `Linked ${selectedTripIds.size} trip(s) and aggregated totals` });
      await loadLinkableTrips();
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to link trips' });
    } finally {
      setLinkSaving(false);
    }
  };

  // Effective verified amount — use live preview when trips visible, else saved total
  const effectiveVerified = linkableTrips.length > 0
    ? (selectedTotals.total_amount_verified || selectedTotals.total_amount_claimed)
    : (num(form.total_amount_verified) || num(form.total_amount_claimed));

  // Auto-compute TDS from rate × verified. User can still override the amount manually.
  useEffect(() => {
    const autoTds = +(effectiveVerified * num(form.tds_rate) / 100).toFixed(2);
    if (form.tds_amount !== autoTds) {
      setForm((f: any) => ({ ...f, tds_amount: autoTds }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveVerified, form.tds_rate]);

  // Auto compute balance
  useEffect(() => {
    const balance = +(effectiveVerified - num(form.payment_released) - num(form.tds_amount)).toFixed(2);
    if (form.balance_to_release !== balance) {
      setForm((f: any) => ({ ...f, balance_to_release: Math.max(balance, 0) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveVerified, form.payment_released, form.tds_amount]);

  const handleChange = (field: string, value: any) =>
    setForm((f: any) => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.season_id || !form.commodity_id || !form.district_id || !form.contractor_id || !form.bill_no || !form.bill_date) {
      setMessage({ type: 'error', text: 'Season, commodity, district, contractor, bill no and date are required' });
      return;
    }
    if (form.billing_period_start && form.billing_period_end && form.billing_period_start > form.billing_period_end) {
      setMessage({ type: 'error', text: 'Period start must be on or before period end' });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const seasonId = form.season_id;
      const tripIds = Array.from(selectedTripIds);
      if (isCreate) {
        const res = await transportBillsAPI.create(seasonId, form);
        const newId = (res.data as any)?.id;
        if (newId && tripIds.length > 0) {
          await transportBillsAPI.linkTrips(seasonId, newId, tripIds);
          setMessage({ type: 'success', text: `Saved and linked ${tripIds.length} trip(s)` });
        } else {
          setMessage({ type: 'success', text: 'Saved' });
        }
        if (newId) navigate(`/transportation/billing/${newId}`, { replace: true });
      } else if (recordId) {
        await transportBillsAPI.update(seasonId, recordId, form);
        setMessage({ type: 'success', text: 'Updated' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
        <span className="ml-2 text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <button
        onClick={() => navigate('/transportation/billing')}
        className="mb-4 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to List
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Receipt className="w-7 h-7 text-blue-600" />
          {isCreate ? 'New Transport Bill' : `Transport Bill #${form.bill_no || recordId}`}
        </h1>
        <p className="text-sm text-gray-500 mt-1">Status: <span className="font-semibold uppercase">{status}</span></p>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Season</label>
            <select value={form.season_id} onChange={(e) => handleChange('season_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Commodity</label>
            <select value={form.commodity_id} onChange={(e) => handleChange('commodity_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {commodities.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">District</label>
            <select value={form.district_id} onChange={(e) => handleChange('district_id', parseInt(e.target.value))}
              disabled={isLocked || isDM}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {districts.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">H&amp;T Contractor / Transporter</label>
            <select value={form.contractor_id} onChange={(e) => handleChange('contractor_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {contractors.map((c) => (
                <option key={c.id} value={c.id}>{c.firm_name} {c.contractor_name ? `— ${c.contractor_name}` : ''}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bill No</label>
            <input type="text" value={form.bill_no || ''} onChange={(e) => handleChange('bill_no', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Bill Date</label>
            <input type="date" value={form.bill_date || ''} onChange={(e) => handleChange('bill_date', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
            />
          </div>

          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Billing Period <span className="text-gray-400 font-normal">(from the contractor's invoice — scopes which trips appear below)</span>
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" value={form.billing_period_start || ''} onChange={(e) => handleChange('billing_period_start', e.target.value)}
                disabled={isLocked} placeholder="Period start"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
              <span className="text-gray-400 text-sm">to</span>
              <input type="date" value={form.billing_period_end || ''} onChange={(e) => handleChange('billing_period_end', e.target.value)}
                disabled={isLocked} placeholder="Period end"
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
              {!isLocked && (
                <>
                  <button type="button" onClick={() => {
                    const d = new Date(); const day = d.getDay(); const monday = new Date(d); monday.setDate(d.getDate() - ((day + 6) % 7));
                    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
                    setForm((f: any) => ({ ...f, billing_period_start: monday.toISOString().slice(0, 10), billing_period_end: sunday.toISOString().slice(0, 10) }));
                  }} className="px-2 py-1 text-xs text-blue-700 border border-blue-200 hover:bg-blue-50 rounded">This Week</button>
                  <button type="button" onClick={() => {
                    const d = new Date(); const start = new Date(d.getFullYear(), d.getMonth(), 1); const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                    setForm((f: any) => ({ ...f, billing_period_start: start.toISOString().slice(0, 10), billing_period_end: end.toISOString().slice(0, 10) }));
                  }} className="px-2 py-1 text-xs text-blue-700 border border-blue-200 hover:bg-blue-50 rounded">This Month</button>
                  <button type="button" onClick={() => {
                    const d = new Date(); const start = new Date(d.getFullYear(), d.getMonth() - 1, 1); const end = new Date(d.getFullYear(), d.getMonth(), 0);
                    setForm((f: any) => ({ ...f, billing_period_start: start.toISOString().slice(0, 10), billing_period_end: end.toISOString().slice(0, 10) }));
                  }} className="px-2 py-1 text-xs text-blue-700 border border-blue-200 hover:bg-blue-50 rounded">Last Month</button>
                  <button type="button" onClick={() => setForm((f: any) => ({ ...f, billing_period_start: '', billing_period_end: '' }))}
                    className="px-2 py-1 text-xs text-gray-600 border border-gray-200 hover:bg-gray-50 rounded">Clear</button>
                </>
              )}
            </div>
          </div>
        </div>

        <h3 className="mt-6 mb-3 text-sm font-semibold text-gray-700 border-b pb-2">Aggregated Totals</h3>
        <p className="text-xs text-gray-500 mb-3">
          Trip-derived fields (trips / bags / weight / claimed / verified) are <span className="font-semibold">read-only</span> — they preview live from the ticked trips below. Save &amp; Link (or Link &amp; Aggregate) commits them. TDS and Payment Released are finance fields you enter here.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {([
            ['total_trips', 'Total Trips', true],
            ['total_bags', 'Total Bags', true],
            ['total_weight_qtl', 'Total Weight (Qtl)', true],
            ['total_amount_claimed', 'Claimed by Transporter (Rs)', true],
            ['total_amount_verified', 'Verified by MARKFED (Rs)', true],
          ] as [string, string, boolean][]).map(([k, label, derived]) => {
            const value = derived ? (displayTotals as any)[k] : form[k];
            return (
              <div key={k}>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  {label}
                  {derived && linkableTrips.length > 0 && (
                    <span className="ml-1 text-[10px] text-blue-600 font-normal">(live preview)</span>
                  )}
                  {derived && linkableTrips.length === 0 && (
                    <span className="ml-1 text-[10px] text-gray-400 font-normal">(from trips)</span>
                  )}
                </label>
                <input type="number" step="0.01" value={value || ''}
                  onChange={(e) => handleChange(k, parseFloat(e.target.value) || 0)}
                  disabled={isLocked || derived}
                  readOnly={derived}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right ${derived ? 'bg-blue-50/50 text-gray-800' : 'disabled:bg-gray-50'}`}
                />
              </div>
            );
          })}

          {/* TDS group: rate + amount (rate drives amount; amount can be overridden) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              TDS Rate <span className="text-gray-400 font-normal">(% of verified)</span>
            </label>
            <input type="number" step="0.01" min="0" max="30" value={form.tds_rate ?? ''}
              onChange={(e) => handleChange('tds_rate', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              TDS Amount (Rs) <span className="text-[10px] text-gray-400 font-normal">= verified × rate</span>
            </label>
            <input type="number" step="0.01" value={form.tds_amount ?? 0}
              onChange={(e) => handleChange('tds_amount', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Payment Released (Rs) <span className="text-[10px] text-gray-400 font-normal">(from installments)</span>
            </label>
            <input type="number" value={form.payment_released || 0} readOnly
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-right" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Balance to Release</label>
            <input type="number" value={form.balance_to_release || 0} readOnly
              className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-right font-bold text-orange-700" />
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>Verified: <span className="font-semibold">{formatAmount(num(form.total_amount_verified))}</span></div>
          <div>Released: <span className="font-semibold">{formatAmount(num(form.payment_released))}</span></div>
          <div>TDS: <span className="font-semibold">{formatAmount(num(form.tds_amount))}</span></div>
          <div>Balance: <span className="font-semibold text-orange-600">{formatAmount(num(form.balance_to_release))}</span></div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
          <textarea value={form.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)}
            disabled={isLocked} rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50"
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleSave}
            disabled={saving || isLocked}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isCreate
              ? (selectedTripIds.size > 0 ? `Save & Link ${selectedTripIds.size} Trip${selectedTripIds.size === 1 ? '' : 's'}` : 'Save Draft')
              : 'Update'}
          </button>
        </div>
      </div>

      {/* Transporter Invoice Upload — after bill is saved */}
      {!isCreate && recordId && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-blue-600" />
            Transporter Invoice
          </h3>
          <p className="text-xs text-gray-500 mb-3">
            Upload the contractor's original invoice (PDF, PNG, or JPG — max 10 MB). Stored with the bill for audit.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/png,image/jpeg"
            onChange={handleInvoiceSelect}
            className="hidden"
          />
          {form.invoice_file_name ? (
            <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{form.invoice_file_name}</p>
                  <p className="text-xs text-gray-500">
                    {form.invoice_size_bytes ? `${(num(form.invoice_size_bytes) / 1024).toFixed(1)} KB · ` : ''}
                    Uploaded {form.invoice_uploaded_at ? new Date(form.invoice_uploaded_at).toLocaleString('en-IN') : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={handleInvoiceView}
                  className="px-3 py-1.5 text-xs font-semibold text-gray-700 bg-white border border-gray-300 hover:bg-gray-100 rounded-lg"
                >
                  View
                </button>
                {!isLocked && (
                  <>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={invoiceUploading}
                      className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                    >
                      {invoiceUploading ? 'Uploading...' : 'Replace'}
                    </button>
                    <button
                      onClick={handleInvoiceDelete}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Remove invoice"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={invoiceUploading || isLocked}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
            >
              {invoiceUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
              {invoiceUploading ? 'Uploading...' : 'Upload Invoice'}
            </button>
          )}
        </div>
      )}

      {/* Linked Trips Picker — visible once the 4 scope fields are set */}
      {form.season_id && form.commodity_id && form.district_id && form.contractor_id && (
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Truck className="w-4 h-4 text-blue-600" />
              Approved Trips for this Bill
            </h3>
            {!isCreate && recordId && (
              <button
                onClick={handleLinkAndAggregate}
                disabled={linkSaving || isLocked}
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
              >
                {linkSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layers className="w-4 h-4" />}
                Link &amp; Aggregate ({selectedTripIds.size})
              </button>
            )}
            {isCreate && (
              <span className="text-xs text-gray-500 italic">
                {selectedTripIds.size} selected — save draft below to link
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-3">
            Approved trips for this contractor
            {form.billing_period_start && form.billing_period_end
              ? <> between <span className="font-semibold">{form.billing_period_start}</span> and <span className="font-semibold">{form.billing_period_end}</span></>
              : <> (set a billing period above to scope by date)</>
            }. Tick the ones covered by the invoice, then Link &amp; Aggregate.
          </p>
          {tripsLoading ? (
            <div className="py-6 text-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading trips...
            </div>
          ) : linkableTrips.length === 0 ? (
            <div className="py-6 text-center text-gray-400 text-sm">
              No approved trips match this bill's scope. Add trips first (they must be approved).
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">Trip Date</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">From</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">To Godown</th>
                    <th className="px-2 py-2 text-left font-semibold text-gray-600">Vehicle</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600">Bags</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600">Wt (Qtl)</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600">Claimed</th>
                    <th className="px-2 py-2 text-right font-semibold text-gray-600">Verified</th>
                    <th className="px-2 py-2 text-center font-semibold text-gray-600">Current Bill</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {linkableTrips.map((t: any) => {
                    const checked = selectedTripIds.has(t.id);
                    const ownedByThis = t.transport_bill_id === recordId;
                    return (
                      <tr key={t.id} className={checked ? 'bg-purple-50' : 'hover:bg-gray-50'}>
                        <td className="px-2 py-2">
                          <input type="checkbox" checked={checked} disabled={isLocked}
                            onChange={() => toggleTrip(t.id)}
                            className="rounded border-gray-300 text-purple-600 focus:ring-purple-500" />
                        </td>
                        <td className="px-2 py-2 text-gray-700">{t.trip_date || '—'}</td>
                        <td className="px-2 py-2 text-gray-700">{t.from_centre || '—'}</td>
                        <td className="px-2 py-2 text-gray-700">{t.toGodown?.godown_name || '—'}</td>
                        <td className="px-2 py-2 text-gray-700">{t.vehicle_no || '—'}</td>
                        <td className="px-2 py-2 text-right text-gray-700">{num(t.bags_sent).toLocaleString('en-IN')}</td>
                        <td className="px-2 py-2 text-right text-gray-700">{num(t.weight_qtl).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                        <td className="px-2 py-2 text-right text-gray-700">{formatAmount(num(t.amount_claimed))}</td>
                        <td className="px-2 py-2 text-right text-gray-900 font-medium">{formatAmount(num(t.amount_verified))}</td>
                        <td className="px-2 py-2 text-center">
                          {ownedByThis ? (
                            <span className="text-xs text-green-700 font-medium">This bill</span>
                          ) : (
                            <span className="text-xs text-gray-400">Unlinked</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Payment Installments — only after the bill is saved */}
      {!isCreate && recordId && (
        <ServicePaymentsSection
          parentId={recordId}
          api={transportBillPaymentsAPI}
          totalOwed={num(form.total_amount_verified) || num(form.total_amount_claimed)}
          canEdit={!isLocked}
          label="transporter"
          onChange={(released, balance) => {
            setForm((prev: any) =>
              prev.payment_released === released && prev.balance_to_release === balance
                ? prev
                : { ...prev, payment_released: released, balance_to_release: balance },
            );
          }}
        />
      )}
    </div>
  );
};

export default TransportBillForm;
